import { useCallback, useMemo } from 'react'
import { useAvailableSkills } from '@renderer/chat/hooks/useSkills'
import { escapeRegex } from '@renderer/chat/components/chat/input/mentions'
import type { Skill } from '@renderer/chat/types'
import { useSidebarSearchQuery, useSidebarSearchSkillId, useSidebarSkillHighlight } from './sidebar-search-atoms'

// As in the composer, `/` opens the list at the start of the text or after a space.
const SLASH_QUERY = /(?:^|\s)\/([^/]*)$/

/** Whether the text holds `/<name>` as a whole command, starting the text or after a space, then a space or the end. */
const namesSkill = (text: string, skill: Skill) =>
  new RegExp(`(?:^|\\s)/${escapeRegex(skill.name)}(?=\\s|$)`).test(text)

interface SidebarSlashSkills {
  /** True while the text ends in a `/` query. */
  isOpen: boolean
  skills: Array<Skill>
  highlightedIndex: number
  moveHighlight: (step: 1 | -1) => void
  pickSkill: (skill: Skill) => void
  /** The picked skill, while the text still names it. */
  pickedSkill: Skill | undefined
  /** Drops the picked skill once `text` no longer names it, so editing it away can't leave it attached. */
  forgetSkillUnlessNamed: (text: string) => void
}

/** The bar's `/` list: the skills the user can invoke, filtered by what follows the `/`. */
export function useSidebarSlashSkills(): SidebarSlashSkills {
  const [query, setQuery] = useSidebarSearchQuery()
  const [pickedSkillId, setPickedSkillId] = useSidebarSearchSkillId()
  const [highlightedIndex, setHighlightedIndex] = useSidebarSkillHighlight()
  const availableSkills = useAvailableSkills()

  const invocableSkills = useMemo(
    () => availableSkills.filter((skill) => skill.availability.userInvoke),
    [availableSkills],
  )
  const pickedSkill = invocableSkills.find((skill) => skill.id === pickedSkillId)
  const namesPickedSkill = !!pickedSkill && namesSkill(query, pickedSkill)
  const slashMatch = namesPickedSkill ? null : SLASH_QUERY.exec(query)
  const slashQuery = slashMatch?.[1].trim().toLowerCase() ?? null

  const skills = useMemo(
    () => (slashQuery === null ? [] : invocableSkills.filter((skill) => skill.name.toLowerCase().includes(slashQuery))),
    [invocableSkills, slashQuery],
  )

  const moveHighlight = useCallback(
    (step: 1 | -1) => {
      if (skills.length === 0) return
      setHighlightedIndex((index) => (Math.min(index, skills.length - 1) + step + skills.length) % skills.length)
    },
    [skills.length, setHighlightedIndex],
  )

  const pickSkill = useCallback(
    (skill: Skill) => {
      if (!slashMatch) return
      // The match can start with the space before the `/`, which stays in the text.
      const slashStart = slashMatch.index + slashMatch[0].indexOf('/')
      setQuery(`${query.slice(0, slashStart)}/${skill.name} `)
      setPickedSkillId(skill.id)
      setHighlightedIndex(0)
    },
    [slashMatch, query, setQuery, setPickedSkillId, setHighlightedIndex],
  )

  const forgetSkillUnlessNamed = useCallback(
    (text: string) => {
      if (pickedSkill && !namesSkill(text, pickedSkill)) setPickedSkillId(null)
    },
    [pickedSkill, setPickedSkillId],
  )

  return {
    isOpen: slashQuery !== null,
    skills,
    highlightedIndex: Math.min(highlightedIndex, Math.max(skills.length - 1, 0)),
    moveHighlight,
    pickSkill,
    pickedSkill: namesPickedSkill ? pickedSkill : undefined,
    forgetSkillUnlessNamed,
  }
}
